/**
 * License-enforcement layer for the surveillance feed.
 *
 * `registers.ts` loads the license register; this module adds the runtime
 * enforcement on top — the bridge that turns the JSON facts into product
 * behaviour. Without this layer, `commercialUse: false` is just text in a
 * file; nothing in the pipeline reads it.
 *
 * The four operator-facing primitives:
 *
 *   getSourceClearance(id)        — classify a source's commercial posture
 *   isPaidTierCleared(id)         — boolean: can this signal flow into paid output?
 *   filterByLicense(items, mode)  — drop sources that fail the chosen mode's gate
 *   getRequiredAttributions(...)  — collect the verbatim attribution + warnings
 *                                   for every source that contributed to a set
 *                                   of signals (briefing footers, exports)
 *
 * The classification is FAIL-CLOSED for unverified entries: a source without
 * `licenseVerified: true` is treated as 'unverified' (not 'cleared'), which
 * filterByLicense('paid') excludes by default. This is intentional — better
 * to under-distribute than to ship data under a license nobody has read.
 *
 * Three modes:
 *   'paid'         — only fully-cleared sources (verified + commercialUse=true)
 *   'display-only' — adds verified non-commercial sources; consumer must
 *                    render without redistributing (no export, no download)
 *   'all'          — every source, including unverified; DEV-MODE ONLY
 *
 * Browser-safe: pure functions over the static JSON register, no node:* imports.
 */

import { getLicense, loadSourceLicenses, type LicenseEntry } from './registers.js';

/** A source's commercial-licensing posture. */
export type PaidTierClearance =
  /** Verified, redistributable, commercial-use OK. Free flow into paid output. */
  | 'cleared'
  /** Verified, but commercial use NOT permitted (e.g. CC-BY-NC-SA 3.0 IGO / WHO).
   *  May still render in display-only mode; never in exports/redistribution. */
  | 'non-commercial'
  /** Verified, but commercialUse=null (rights are ambiguous — needs a
   *  written partnership agreement before the source can ship in paid output). */
  | 'partnership-required'
  /** licenseVerified=false. The fail-closed default — treat as restricted
   *  until someone reads the terms page. */
  | 'unverified'
  /** Source id isn't in the register at all. A CI gate should prevent this
   *  in production; if it surfaces at runtime, the source is implicitly
   *  excluded from every mode (safer than allowing). */
  | 'unregistered';

/** Which clearance levels a given license mode admits. */
export type LicenseMode = 'paid' | 'display-only' | 'all';

const MODE_ADMITS: Record<LicenseMode, ReadonlySet<PaidTierClearance>> = {
  paid:           new Set<PaidTierClearance>(['cleared']),
  'display-only': new Set<PaidTierClearance>(['cleared', 'non-commercial']),
  all:            new Set<PaidTierClearance>(['cleared', 'non-commercial', 'partnership-required', 'unverified']),
};

/** Classify a source's paid-tier posture from its license register entry. */
export function classify(entry: LicenseEntry | undefined): PaidTierClearance {
  if (!entry) return 'unregistered';
  if (!entry.licenseVerified) return 'unverified';
  if (entry.commercialUse === true) return 'cleared';
  if (entry.commercialUse === false) return 'non-commercial';
  return 'partnership-required';
}

/** Public lookup: classify by source id. */
export function getSourceClearance(sourceId: string): PaidTierClearance {
  return classify(getLicense(sourceId));
}

/** Boolean predicate: would this source's data ship in a paid export? */
export function isPaidTierCleared(sourceId: string): boolean {
  return getSourceClearance(sourceId) === 'cleared';
}

/** Throwing assertion: use at export boundaries where unauthorized data
 *  would be a contract/license violation. The thrown message includes the
 *  clearance + a pointer to the verifyUrl when applicable. */
export class LicenseViolationError extends Error {
  readonly sourceId: string;
  readonly clearance: PaidTierClearance;
  constructor(sourceId: string, clearance: PaidTierClearance) {
    super(
      `License violation: source '${sourceId}' has clearance '${clearance}' and cannot ship in paid output.` +
      (clearance === 'unverified'
        ? ' Verify the source via data/source-licenses.json before lifting this restriction.'
        : clearance === 'non-commercial'
          ? ' This source forbids commercial use; render in display-only mode instead, or secure a partnership agreement.'
          : clearance === 'partnership-required'
            ? ' Commercial-use rights are ambiguous; secure a written partnership agreement.'
            : ''),
    );
    this.name = 'LicenseViolationError';
    this.sourceId = sourceId;
    this.clearance = clearance;
  }
}

export function assertCommercialUse(sourceId: string): void {
  const clearance = getSourceClearance(sourceId);
  if (clearance !== 'cleared') throw new LicenseViolationError(sourceId, clearance);
}

/** Filter signals (or any source-keyed objects) by license mode. Drops items
 *  whose source isn't admitted by the chosen mode. Pure — returns a new array. */
export function filterByLicense<T extends { source: string }>(
  items: readonly T[],
  mode: LicenseMode,
): T[] {
  const admit = MODE_ADMITS[mode];
  return items.filter(item => admit.has(getSourceClearance(item.source)));
}

/** One source's required attribution + warnings, suitable for a briefing
 *  footer or export attribution block. */
export interface AttributionLine {
  sourceId: string;
  publisher: string;
  license: string;
  attributionText: string;
  warnings: readonly string[];
  clearance: PaidTierClearance;
  verifyUrl: string;
}

/** Collect the distinct sources contributing to a set of items + return the
 *  attribution lines for each (deduped, alphabetically sorted by source id).
 *  Use at briefing/export time so the rendered output carries the legally-
 *  required citations + warnings for every source whose data appears. */
export function getRequiredAttributions(
  items: readonly { source: string }[],
): AttributionLine[] {
  const ids = new Set<string>();
  for (const item of items) ids.add(item.source);
  const out: AttributionLine[] = [];
  for (const id of [...ids].sort()) {
    const entry = getLicense(id);
    if (!entry) continue;  // unregistered sources contribute no attribution
    out.push({
      sourceId: id,
      publisher: entry.publisher,
      license: entry.license,
      attributionText: entry.attributionText,
      warnings: entry.operatorWarnings,
      clearance: classify(entry),
      verifyUrl: entry.verifyUrl,
    });
  }
  return out;
}

/** Whole-register summary: count entries by clearance. Used by the §7.22
 *  Feed Health "License readiness" surface + the audit script. */
export interface LicenseReadiness {
  total: number;
  cleared: number;
  nonCommercial: number;
  partnershipRequired: number;
  unverified: number;
  /** % of entries that are verified (any clearance), 0-100. */
  verifiedPct: number;
  /** % of entries cleared for paid output, 0-100. */
  paidReadyPct: number;
  /** Ids of entries that block paid-tier launch (everything not 'cleared'). */
  blockingIds: readonly string[];
}

export function summariseLicenseReadiness(): LicenseReadiness {
  const all = loadSourceLicenses();
  const ids = Object.keys(all);
  const counts: Record<PaidTierClearance, number> = {
    cleared: 0, 'non-commercial': 0, 'partnership-required': 0,
    unverified: 0, unregistered: 0,
  };
  const blocking: string[] = [];
  for (const id of ids) {
    const c = classify(all[id]);
    counts[c]++;
    if (c !== 'cleared') blocking.push(id);
  }
  const total = ids.length;
  const verified = counts.cleared + counts['non-commercial'] + counts['partnership-required'];
  return {
    total,
    cleared: counts.cleared,
    nonCommercial: counts['non-commercial'],
    partnershipRequired: counts['partnership-required'],
    unverified: counts.unverified,
    verifiedPct: total === 0 ? 0 : Math.round((verified / total) * 100),
    paidReadyPct: total === 0 ? 0 : Math.round((counts.cleared / total) * 100),
    blockingIds: blocking.sort(),
  };
}
